<div class="fixed inset-0 z-60 bg-(--mba-scrim) animate-[mba-fade_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none"
    (click)="closed.emit()"></div>
<aside role="dialog" aria-modal="true" [attr.aria-label]="fullName() + ' details'"
    class="fixed inset-y-0 right-0 z-61 flex w-[min(600px,100%)] flex-col bg-(--mba-surface) font-(family-name:--font-fallback) text-(--fg-1) shadow-[-20px_0_60px_rgba(18,6,43,0.22)] animate-[mba-dr_360ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none">

    <header class="flex shrink-0 items-center gap-3 border-b border-(--border-subtle) px-[22px] py-4">
        <div class="flex min-w-0 flex-1 items-center gap-3">
            <span role="link" tabindex="0" [title]="'View ' + fullName() + '’s SHOUT profile'"
                [class]="ui.avatar + ' ' + ui.avatarLink + ' size-12 text-[19px]'"
                (click)="openProfile.emit()" (keydown.enter)="openProfile.emit()">
                @if (photo()) {
                <img class="size-full rounded-full object-cover" [src]="photo()" alt="" />
                } @else {
                {{ initial() }}
                }
                @if (creator()?.isVerified) {
                <span [class]="ui.verifiedBadge" aria-label="Verified"><mba-icon name="check" size="size-2.5" [strokeWidth]="3" /></span>
                }
            </span>
            <span class="flex min-w-0 flex-col gap-1">
                <b class="font-(family-name:--font-display) text-2xl leading-none font-bold">{{ fullName() }}</b>
                <span class="flex flex-wrap items-center gap-2.5 text-xs text-(--fg-2)">
                    @if (creator()?.homeTownName) {
                    <span class="inline-flex items-center gap-1">{{ creator()!.homeTownName }}</span>
                    }
                    @if (creator()?.tikTokFollowers) {
                    <span class="inline-flex items-center gap-1"><img [class]="ui.platformLogo" src="assets/social-icon/tik-tok-transparent.svg" alt="" />{{ creator()!.tikTokFollowers | shortNumber }}</span>
                    }
                    @if (creator()?.instaFollowers) {
                    <span class="inline-flex items-center gap-1"><img [class]="ui.platformLogo" src="assets/social-icon/instagram-logo.png" alt="" />{{ creator()!.instaFollowers | shortNumber }}</span>
                    }
                    @if (creator()?.youtubeSubscribers) {
                    <span class="inline-flex items-center gap-1"><img [class]="ui.platformLogo" src="assets/social-icon/youtube_shorts.webp" alt="" />{{ creator()!.youtubeSubscribers | shortNumber }}</span>
                    }
                    @if (rating(); as r) {
                    <span class="inline-flex items-center gap-1.5 text-(--fg-3)"><mba-icon name="star" size="size-3.5 fill-(--star) text-(--star)" />
                        <b class="font-(family-name:--font-display) text-lg font-extrabold text-(--fg-1)">{{ r.average_rating | number: '1.1-1' }}</b>{{ r.total_reviews }} {{ r.total_reviews === 1 ? 'review' : 'reviews' }}</span>
                    } @else {
                    <span [class]="ui.newCreatorPill"><mba-icon name="sparkles" size="size-[13px]" />New creator</span>
                    }
                </span>
            </span>
        </div>
        <button type="button" aria-label="Close" [class]="ui.iconButton" (click)="closed.emit()"><mba-icon name="x" /></button>
    </header>

    <!-- Scrolls between header and footer. Sections sit in a plain auto-height column, so none is ever
         squeezed: some children (hire breakdown, countdown) are display: contents, which a shrink-0 rule
         on direct children never reaches. -->
    <div class="@container min-h-0 flex-1 overflow-y-auto px-[22px] pt-5 pb-[26px]">
        <div class="flex flex-col gap-5">
            <!-- 1. Stage timeline -->
            <div class="grid grid-cols-6 gap-1.5 @max-[540px]:grid-cols-3 @max-[540px]:gap-y-3" role="list" aria-label="Stages">
                @for (s of timeline(); track s.label) {
                <div role="listitem" class="flex min-w-0 flex-col gap-[5px]">
                    <i class="h-2 rounded" aria-hidden="true" [class]="s.current ? currentBar : s.reached ? 'bg-(--app-accent)' : 'bg-(--bg-alt) shadow-[inset_0_0_0_1px_var(--border-subtle)]'"></i>
                    <b class="font-(family-name:--font-display) text-base leading-none font-bold"
                        [class]="s.reached ? 'text-(--fg-1)' : 'text-(--fg-3)'">{{ s.label }}</b>
                    <span [class]="ui.sub">{{ s.when }}</span>
                </div>
                }
            </div>

            <!-- 2. Countdown (applied + auto-decline on) -->
            @if (stage() === 1 && autoDeclineOn()) {
            <mbt-application-countdown size="panel" [autoDeclineAt]="applicant().autoDeclineAt"
                [appliedAt]="applicant().applicationStatusModifiedDate || applicant().createdDate"
                [autoDeclineDays]="autoDeclineDays()" [nowMs]="nowMs()" />
            }

            <!-- 3. Next step: for an Applied creator, the hire itself -->
            @if (stage() === 1 && hireInPanel()) {
            <mbt-hire-breakdown [applicant]="applicant()" [brief]="brief()" [disabled]="expired()" [highlight]="needsYou()"
                [heading]="expired() ? 'Application expired' : step().why"
                [caption]="expired() ? 'This application closed before anyone responded.' : step().sub"
                (hire)="hire.emit()" (decline)="decline.emit()" />
            } @else {
            <div class="flex items-center gap-3.5 rounded-xl border px-4 py-3.5 @max-[540px]:flex-wrap"
                [class]="needsYou() ? 'border-(--app-accent-ring) bg-(--app-accent-soft)' : 'border-(--border-subtle) bg-(--bg-alt)'">
                <span class="grid size-[38px] shrink-0 place-items-center rounded-full bg-(--mba-surface) text-(--app-accent)" aria-hidden="true"><mba-icon [name]="actionIcon()" /></span>
                <div class="flex min-w-0 flex-1 flex-col gap-[3px]">
                    <span [class]="ui.kicker">Next step</span>
                    <b class="font-(family-name:--font-display) text-xl leading-none font-extrabold">{{ expired() ? 'Application expired' : step().why }}</b>
                    <span [class]="ui.sub">{{ expired() ? 'This application closed before anyone responded.' : step().sub }}</span>
                </div>
                <mbt-next-step-cell [step]="step()" [busy]="busy()" [disabled]="expired()" [showMenu]="false"
                    [price]="costText()" (primary)="primary.emit()" (decline)="decline.emit()" />
            </div>
            }

            <!-- 4. Stats -->
            <div class="grid grid-cols-4 gap-px overflow-hidden rounded-xl border border-(--border-subtle) bg-(--border-subtle) @max-[540px]:grid-cols-2">
                @for (stat of stats(); track stat.label) {
                <div class="flex min-w-0 flex-col gap-1.5 bg-(--mba-surface) px-3.5 py-3">
                    <span [class]="ui.kicker">{{ stat.label }}</span>
                    <b class="font-(family-name:--font-display) text-[22px] leading-[0.9] font-extrabold tabular-nums">{{ stat.value }}</b>
                </div>
                }
            </div>

            <!-- 5. Screening answers -->
            @if (stage() >= 1 && qa().length) {
            <section class="flex flex-col gap-2.5">
                <div class="flex items-center justify-between gap-2.5">
                    <h3 [class]="ui.sectionTitle">Screening Answers</h3>
                    @if (stage() >= 2) {
                    <span class="inline-flex items-center gap-[5px] font-semibold text-(--success)"><mba-icon name="check-circle" size="size-[15px]" />Passed</span>
                    }
                </div>
                <div class="flex flex-col divide-y divide-(--border-subtle) overflow-hidden rounded-xl border border-(--border-subtle)">
                    @for (row of qa(); track $index) {
                    <div class="flex flex-col gap-[5px] px-3.5 py-3">
                        <span [class]="ui.kicker">{{ row.question || 'Question ' + ($index + 1) }}</span>
                        <p class="m-0 font-(family-name:--font-display) text-[17px] leading-[1.2] font-semibold text-pretty">{{ row.answer || 'No answer' }}</p>
                        @if (row.expected && row.expected.kind !== 'manual') {
                        <span class="self-start rounded-full px-2 py-0.5 text-[11px] font-bold" [class]="verdictClass(row.verdict)">{{ verdictLabel(row.verdict) }}</span>
                        }
                    </div>
                    }
                </div>
            </section>
            }

            <!-- 6. Brief videos (read-only) -->
            @if (stage() >= 2) {
            <section class="flex flex-col gap-2.5">
                <div class="flex items-center justify-between gap-2.5">
                    <h3 [class]="ui.sectionTitle">Brief Videos</h3>
                    <mbt-video-status-pill [counts]="counts()" />
                </div>
                <span [class]="ui.sub">Review, approve and chat about drafts in View Submission.</span>
                <div class="flex flex-col divide-y divide-(--border-subtle) overflow-hidden rounded-xl border border-(--border-subtle)">
                    @if (videos() === null) {
                    <div class="px-3.5 py-3"><span [class]="ui.skeleton + ' w-3/5'"></span></div>
                    } @else {
                    @for (row of videoRows(); track $index) {
                    <div class="flex items-center gap-3.5 px-3.5 py-3 @max-[540px]:flex-wrap" [class]="row.state === 'new' ? 'bg-(--mba-row-hi)' : ''">
                        <span class="grid h-[62px] w-11 shrink-0 place-items-center overflow-hidden rounded-lg"
                            [class]="row.thumbnail ? 'bg-[#111] text-white/95' : 'border border-dashed border-(--border-strong) bg-[repeating-linear-gradient(-45deg,var(--bg-alt)_0_4px,var(--mba-surface)_4px_8px)] text-(--fg-muted)'">
                            @if (row.thumbnail) {
                            <img class="size-full object-cover" [src]="row.thumbnail" alt="" />
                            } @else {
                            <mba-icon [name]="row.state === 'none' ? 'clock' : 'play'" size="size-4" />
                            }
                        </span>
                        <span class="flex min-w-0 flex-1 flex-col gap-1">
                            <b class="font-(family-name:--font-display) text-[17px] leading-none font-bold">{{ row.title }}</b>
                            <span class="inline-flex items-center gap-[5px] text-xs font-bold" [class]="videoTone(row.state).text">
                                <i class="size-[7px] rounded-full" [class]="videoTone(row.state).dot"></i>{{ videoLabel(row.state) }}</span>
                        </span>
                    </div>
                    } @empty {
                    <div class="px-3.5 py-3"><span [class]="ui.sub">No videos yet.</span></div>
                    }
                    }
                </div>
            </section>
            }

            <!-- 7. Platform -->
            @if (kind() === 'influence') {
            <section class="flex flex-col gap-2.5">
                <h3 [class]="ui.sectionTitle">{{ channelLabel() }}</h3>
                <div class="flex flex-col divide-y divide-(--border-subtle) overflow-hidden rounded-xl border border-(--border-subtle)">
                    @if (applicant().ttoInviteLink) {
                    <div [class]="boxRow">
                        <span [class]="boxRowText"><b [class]="boxRowTitle">TikTok One invite</b><span [class]="ui.sub">Links the creator to your TikTok One campaign</span></span>
                        <button type="button" [class]="ui.btnOutline" (click)="copyInvite.emit(applicant().ttoInviteLink!)"><mba-icon name="copy" />Copy link</button>
                    </div>
                    }
                    @if (channelLabel() === 'TikTok') {
                    <div [class]="boxRow">
                        <span [class]="boxRowText"><b [class]="boxRowTitle">Spark Ads code</b>
                            <span [class]="ui.sub">{{ sparkCode() ? 'Received · ready to boost from the Ads tab' : 'Requested when the post goes live' }}</span></span>
                        @if (sparkCode()) {
                        <span class="inline-flex items-center gap-[5px] font-semibold text-(--success)"><mba-icon name="check-circle" size="size-[15px]" />Received</span>
                        }
                    </div>
                    }
                    @if (stage() >= 4 && livePost(); as post) {
                    <div [class]="boxRow">
                        <span [class]="boxRowText"><b [class]="boxRowTitle">Post</b><span [class]="ui.sub">{{ postStatusText(post) }}</span></span>
                        @if (post.fullUrl || post.videoLink) {
                        <a [class]="ui.btnOutline" [href]="post.fullUrl || post.videoLink" target="_blank" rel="noopener"><mba-icon name="external-link" />View on {{ channelLabel() }}</a>
                        }
                    </div>
                    }
                </div>
            </section>
            } @else if (stage() >= 2) {
            <section class="flex flex-col gap-2.5">
                <h3 [class]="ui.sectionTitle">Delivery</h3>
                <div class="overflow-hidden rounded-xl border border-(--border-subtle)">
                    <div [class]="boxRow">
                        <span [class]="boxRowText"><b [class]="boxRowTitle">Files</b><span [class]="ui.sub">Edited videos for each piece</span></span>
                        <span [class]="ui.sub">{{ approvedCount() }} of {{ videoRows().length }} approved</span>
                    </div>
                </div>
            </section>
            }

            <!-- 8. Payment -->
            @if (stage() >= 2 && costText()) {
            <div class="flex items-center justify-between gap-3 rounded-lg bg-(--shout-deep-purple) px-4 py-3.5 text-white">
                <div class="flex flex-col gap-1">
                    <span [class]="ui.kickerBase + ' text-white/70'">Brief cost</span>
                    <b class="font-(family-name:--font-display) text-[28px] leading-[0.9] font-extrabold">{{ costText() }}</b>
                </div>
                <span class="max-w-[24ch] text-right text-xs text-white/75">{{ costNote() }}</span>
            </div>
            }

            <!-- 9. Destructive -->
            @if (stage() === 0) {
            <button type="button" [class]="ui.textDanger" (click)="withdraw.emit()"><mba-icon name="x-circle" size="size-[15px]" />Withdraw invite</button>
            } @else if (canUnhire()) {
            <button type="button" [class]="ui.textDanger" (click)="unhire.emit()"><mba-icon name="user-x" size="size-[15px]" />Un-hire {{ firstName() }}</button>
            }
        </div>
    </div>

    <footer class="flex shrink-0 items-center gap-2 border-t border-(--border-subtle) bg-(--bg-alt) px-[22px] py-3.5">
        <span class="flex-1"></span>
        <span [class]="ui.sub">{{ position() + 1 }} of {{ total() }}</span>
        <button type="button" [class]="ui.btnOutline" [disabled]="position() <= 0" (click)="previous.emit()"><mba-icon name="chevron-up" />Previous</button>
        <button type="button" [class]="ui.btnOutline" [disabled]="position() >= total() - 1" (click)="next.emit()"><mba-icon name="chevron-down" />Next</button>
    </footer>
</aside>
